import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { WindowService } from 'src/shared/providers/window.service';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

/**
 * Mirrors the inline `tidy-error-message` extraction
 * `(err.error && err.error.message) ? err.error.message : err.message`,
 * plus a plain-string fallback, so the toast shows the same text the inline
 * error does. Returns '' when there is no usable message, so the toast layer
 * skips presenting instead of rendering "undefined" / "[object Object]".
 */
export function normalizeToastMessage(input: any): string {
  const extracted = (typeof input === 'string')
    ? input
    : (input && input.error && input.error.message)
      ? input.error.message
      : (input && input.message ? input.message : input);
  const text = (typeof extracted === 'string') ? extracted.trim() : '';
  if (!text || text.toLowerCase() === 'undefined' || text.toLowerCase() === 'null') {
    return '';
  }
  return text;
}

@Injectable()
export class Util {
  constructor(
    private toastCtrl: ToastController,
    private windowService: WindowService
  ) {}

  private isDesktop(): boolean {
    return this.windowService.isDesktopRes;
  }

  async showError(message: any, duration = 5000): Promise<void> {
    // Some callers pass a raw error (string / object) rather than a clean string,
    // which rendered the literal "undefined" (or "[object Object]") at the bottom.
    // Normalize to a real message and show nothing when there isn't one.
    const text = normalizeToastMessage(message);
    if (!text) {
      return;
    }
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(text),
      duration,
      cssClass: isDesktop ? 'toast-error toast-desktop' : 'toast-error',
      icon: 'alert-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async showSuccess(message: string, duration = 5000): Promise<void> {
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: isDesktop ? 'toast-success toast-desktop' : 'toast-success',
      icon: 'checkmark-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async showWarning(message: string, duration = 5000): Promise<void> {
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: isDesktop ? 'toast-warning toast-desktop' : 'toast-warning',
      icon: 'alert-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async showInfo(message: string, duration = 5000): Promise<void> {
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: isDesktop ? 'toast-info toast-desktop' : 'toast-info',
      icon: 'information-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async closeLastOpenedToast(): Promise<void> {
    const toast = await this.toastCtrl.getTop();
    if (toast) {
      toast.dismiss();
    }
  }

  private readonly letterGradients: { [key: string]: string } = {
    'A': 'linear-gradient(135deg, #373b44 0%, #4286f4 100%)',
    'B': 'linear-gradient(135deg, #b92b27 0%, #1565c0 100%)',
    'C': 'linear-gradient(135deg, #1f4037 0%, #99f2c8 100%)',
    'D': 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
    'E': 'linear-gradient(135deg, #009fff 0%, #ec2f4b 100%)',
    'F': 'linear-gradient(135deg, #da4453 0%, #89216b 100%)',
    'G': 'linear-gradient(135deg, #d66d75 0%, #e29587 100%)',
    'H': 'linear-gradient(135deg, #4568dc 0%, #b06ab3 100%)',
    'I': 'linear-gradient(135deg, #000046 0%, #1cb5e0 100%)',
    'J': 'linear-gradient(135deg, #283c86 0%, #45a247 100%)',
    'K': 'linear-gradient(135deg, #4e54c8 0%, #8f94fb 100%)',
    'L': 'linear-gradient(135deg, #0575e6 0%, #021b79 100%)',
    'M': 'linear-gradient(135deg, #3494e6 0%, #ec6ead 100%)',
    'N': 'linear-gradient(135deg, #2196f3 0%, #f44336 100%)',
    'O': 'linear-gradient(135deg, #4da0b0 0%, #d39d38 100%)',
    'P': 'linear-gradient(135deg, #8e0e00 0%, #1f1c18 100%)',
    'Q': 'linear-gradient(135deg, #360033 0%, #0b8793 100%)',
    'R': 'linear-gradient(135deg, #556270 0%, #ff6b6b 100%)',
    'S': 'linear-gradient(135deg, #4b6cb7 0%, #182848 100%)',
    'T': 'linear-gradient(135deg, #c04848 0%, #480048 100%)',
    'U': 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
    'V': 'linear-gradient(135deg, #ff512f 0%, #f09819 100%)',
    'W': 'linear-gradient(135deg, #348f50 0%, #56b4d3 100%)',
    'X': 'linear-gradient(135deg, #314755 0%, #26a0da 100%)',
    'Y': 'linear-gradient(135deg, #536976 0%, #292e49 100%)',
    'Z': 'linear-gradient(135deg, #1488cc 0%, #2b32b2 100%)'
  };

  getAvatarGradient(initials: string): string {
    const firstInitial = initials.charAt(0).toUpperCase() || 'A';
    return this.letterGradients[firstInitial] || this.letterGradients['A'];
  }
  
}
